import React, { useState, useEffect } from 'react';
import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { LoadingSpinner } from '@common/LoadingSpinner';
import { Toast } from '@common/Toast';
import { StructuresMap } from './StructuresMap';
import { StructuresFilters } from './StructuresFilters';
import { StructuresList } from './StructuresList';
import { fetchStructuresData, filterStructuresAlongRoute } from '../../utils/structures/structuresService';
import { logError, logInfo } from '@utils/logger';

export const StructuresOnRoute = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [structures, setStructures] = useState([]);
  const [filteredStructures, setFilteredStructures] = useState([]);
  const [route, setRoute] = useState(null);
  const [filters, setFilters] = useState({
    types: [],
    minClearance: null,
    maxDistance: 0.5 // 500m default buffer
  });
  const [toast, setToast] = useState(null);

  // Fetch structures data on mount
  useEffect(() => {
    const loadStructures = async () => {
      try {
        setLoading(true);
        const data = await fetchStructuresData();
        
        if (!data || !Array.isArray(data)) {
          throw new Error('Invalid structures data received');
        }
        
        setStructures(data);
        setToast({
          type: 'success',
          message: `Loaded ${data.length} structures from database`
        });
        setLoading(false);
      } catch (error) {
        logError('Failed to load structures:', error);
        setToast({
          type: 'error',
          message: 'Using sample structures data'
        });
        setStructures([]);
        setLoading(false);
      }
    };

    loadStructures();
  }, []);

  // Filter structures when route or filters change
  useEffect(() => {
    const filterStructures = async () => {
      if (!route?.points?.length) {
        setFilteredStructures([]);
        // Don't show toast when no route is set
        return;
      }

      if (!route.confirmed) {
        setFilteredStructures([]);
        setToast({
          type: 'info',
          message: 'Please confirm the route to see nearby structures'
        });
        return;
      }

      try {
        const filtered = await filterStructuresAlongRoute(
          structures,
          route.points,
          filters.maxDistance,
          filters
        );
        setFilteredStructures(filtered);
        logInfo(`Found ${filtered.length} structures along route`);
        setToast({
          type: 'info',
          message: filtered.length > 0 
            ? `Found ${filtered.length} structures within ${filters.maxDistance}km of route` 
            : `No structures found within ${filters.maxDistance}km of route`
        });
      } catch (error) {
        logError('Failed to filter structures:', error);
        setToast({
          type: 'error',
          message: 'Failed to filter structures. Please try again.'
        });
        setFilteredStructures([]);
      }
    };

    filterStructures();
  }, [route, filters, structures]);

  const handleRouteChange = (newRoute) => {
    setRoute({
      ...newRoute,
      confirmed: true
    });
  };

  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#3D3F42] flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <LoadingSpinner />
          <p className="text-gray-400">Loading structures database...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#3D3F42]">
      {/* Header */}
      <div className="sticky top-0 bg-[#2A2B2E] shadow-sm z-10 p-4">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-gray-700 rounded-lg"
          >
            <ArrowLeft className="w-6 h-6 text-white" />
          </button>
          <div>
            <h1 className="text-xl font-semibold text-white">Structures on Route</h1>
            <p className="text-sm text-gray-400">
              {!route?.points?.length 
                ? 'Draw a route on the map to find nearby structures'
                : 'Find structures along your route'}
            </p>
          </div>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row h-[calc(100vh-64px)]">
        {/* Left Panel - Filters & List */}
        <div className="w-full lg:w-96 bg-[#2A2B2E] p-4 overflow-y-auto">
          <StructuresFilters
            filters={filters}
            onChange={handleFiltersChange}
          />
          <StructuresList
            structures={filteredStructures}
            onSelect={(structure) => {
              // Center map on selected structure
              if (structure.coordinates) {
                const { latitude, longitude } = structure.coordinates;
                // Map ref method to center on coordinates
              }
            }}
          />
        </div>

        {/* Right Panel - Map */}
        <div className="flex-1">
          <StructuresMap
            structures={filteredStructures}
            route={route}
            onRouteChange={handleRouteChange}
          />
        </div>
      </div>

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};